import React, { useEffect, useState } from 'react';
import { useMatches, useNotificationCounters, useSubscriptionStatus, useLikes, useMyLikes, useSwipeProfile, useRemoveLike, queryKeys } from '@/hooks/api';
import { useUI, useAppStore } from '@/store';
import { LoadingScreen } from '@/components/ui';
import { TelegramUtils } from '@/utils/telegram';
import { PaymentHelper } from '@/utils/payments';
import { useQueryClient } from '@tanstack/react-query';
import type { Match, LikerUser, MyLikesUser } from '@/types';
import ProfileDetailScreen from './ProfileDetailScreen';

const MatchesScreen: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'matches' | 'likes' | 'mylikes'>('matches');
  const { setActiveScreen, setModal, showToast } = useUI();
  const { data, isLoading, error } = useMatches();
  const { data: notificationCounters } = useNotificationCounters();
  const { ui, setViewingProfile } = useAppStore();
  const queryClient = useQueryClient();
  const swipeProfile = useSwipeProfile({
    onSuccess: async (response, request) => {
      // Invalidate likes list to refresh after swipe
      queryClient.invalidateQueries(['likes']);
      
      // Invalidate notification counters to update the badge count
      queryClient.invalidateQueries(queryKeys.notificationCounters);
      
      // Invalidate feed to refresh when user returns to feed screen
      queryClient.invalidateQueries(queryKeys.feedProfiles);
      
      // If it's a match, show celebration modal
      if (response.matched) {
        // Refetch matches to get the new match
        await queryClient.invalidateQueries({ queryKey: queryKeys.matches });
        
        // Wait a bit for the query to refetch
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Get the updated matches from cache
        const matchesData = queryClient.getQueryData<any>(queryKeys.matches);
        
        // Find the match with this user
        let matchId: string | undefined;
        if (matchesData && Array.isArray(matchesData)) {
          const match = matchesData.find((m: any) => 
            m.users?.some((u: any) => u.id === request.targetId)
          );
          matchId = match?.id;
        }
        
        setModal('match-celebration', { 
          userId: request.targetId,
          matchId: matchId 
        });
      }
      
      // Close profile view
      setViewingProfile(null);
    },
    onError: () => {
      showToast('Не удалось выполнить действие', 'error');
      setViewingProfile(null);
    },
  });

  useEffect(() => {
    TelegramUtils.configureBackButton(true, () => {
      setActiveScreen('feed');
    });
    TelegramUtils.configureMainButton({ visible: false });

    // Refresh data when screen is opened
    queryClient.invalidateQueries(queryKeys.matches);
    queryClient.invalidateQueries(['likes']);
    queryClient.invalidateQueries(['my-likes']);
    // Note: notificationCounters removed - query auto-fetches on mount
  }, [setActiveScreen, queryClient]);

  if (isLoading) {
    return <LoadingScreen message="Загружаем ваши совпадения..." />;
  }

  const matches = data || [];

  // Handle profile detail view for likes tab
  if (ui.viewingProfileId && activeTab === 'likes') {
    const handleSwipeFromLikes = (direction: 'like' | 'pass') => {
      if (!ui.viewingProfileId) return;
      
      const swipeDirection: 'like' | 'pass' | 'super_like' = direction;
      swipeProfile.mutate({
        targetId: ui.viewingProfileId,
        direction: swipeDirection,
      });
    };

    return (
      <ProfileDetailScreen
        userId={ui.viewingProfileId}
        onBack={() => setViewingProfile(null)}
        onSwipe={handleSwipeFromLikes}
      />
    );
  }

  // Handle profile detail view for mylikes tab (with remove button instead of swipe)
  if (ui.viewingProfileId && activeTab === 'mylikes') {
    return (
      <MyLikesProfileDetail
        userId={ui.viewingProfileId}
        onBack={() => setViewingProfile(null)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-tg-bg safe-area-y">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
        <button
          onClick={() => {
            setActiveScreen('feed');
            TelegramUtils.haptic.light();
          }}
          className="p-2 -ml-2 hover:bg-white/5 rounded-xl transition-colors"
        >
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        
        <h1 className="text-xl font-bold text-white absolute left-1/2 transform -translate-x-1/2">Совпадения</h1>
        
        <div className="w-10" /> {/* Spacer for centering */}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-white/10">
        <button
          className={`flex-1 py-3 px-2 text-center font-medium transition-colors text-sm ${
            activeTab === 'matches'
              ? 'text-white border-b-2 border-white light:text-neutral-900 light:border-neutral-900'
              : 'text-white/50 light:text-neutral-500'
          }`}
          onClick={() => setActiveTab('matches')}
        >
          Совпадения ({matches.length})
        </button>
        <button
          className={`flex-1 py-3 px-2 text-center font-medium transition-colors text-sm ${
            activeTab === 'likes'
              ? 'text-white border-b-2 border-white light:text-neutral-900 light:border-neutral-900'
              : 'text-white/50 light:text-neutral-500'
          }`}
          onClick={() => setActiveTab('likes')}
        >
          Ты нравишься {notificationCounters && notificationCounters.unseen_likes_count > 0 && `(${notificationCounters.unseen_likes_count})`}
        </button>
        <button
          className={`flex-1 py-3 px-2 text-center font-medium transition-colors text-sm ${
            activeTab === 'mylikes'
              ? 'text-white border-b-2 border-white light:text-neutral-900 light:border-neutral-900'
              : 'text-white/50 light:text-neutral-500'
          }`}
          onClick={() => setActiveTab('mylikes')}
        >
          Понравились
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 p-4">
        {activeTab === 'matches' && <MatchesList matches={matches} />}
        {activeTab === 'likes' && <LikesYouList />}
        {activeTab === 'mylikes' && <MyLikesList />}
      </div>
    </div>
  );
};

const MatchesList: React.FC<{ matches: Match[] }> = ({ matches }) => {
  const { setActiveScreen, setModal } = useUI();

  if (matches.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="text-6xl mb-4">💕</div>
        <h3 className="text-lg font-semibold text-white mb-2">Пока нет совпадений</h3>
        <p className="text-white/70 mb-6">
          Продолжайте свайпать, чтобы найти идеальную пару!
        </p>
        <button
          onClick={() => setActiveScreen('feed')}
          className="h-12 px-6 bg-white text-black rounded-2xl font-semibold hover:bg-neutral-100 transition-all active:scale-95"
        >
          Начать свайпать
        </button>
      </div>
    );
  }

  const openChat = (match: Match) => {
    setModal('chat', { matchId: match.id, match });
  };

  return (
    <div className="grid grid-cols-2 gap-3">
      {matches.map((match) => {
        // Get the matched user (first user in array is always the matched user from API)
        const otherUser = match.users[0];
        return (
          <div
            key={match.id}
            className="bg-white/5 rounded-2xl overflow-hidden cursor-pointer hover:bg-white/10 transition-all active:scale-95 border border-white/10"
            onClick={() => openChat(match)}
          >
            <div className="aspect-square relative">
              <img
                src={otherUser.photos[0]}
                alt={otherUser.name}
                className="w-full h-full object-cover"
              />
              {match.unreadCount > 0 && (
                <div className="absolute top-2 right-2 min-w-[24px] h-6 px-2 bg-danger-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">{match.unreadCount}</span>
                </div>
              )}
            </div>
            <div className="p-3">
              <h4 className="font-semibold text-white truncate mb-1">
                {otherUser.name}
              </h4>
              <p className="text-sm text-white/60 truncate">
                {match.lastMessage?.text || 'Say hello! 👋'}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const LikesYouList: React.FC = () => {
  const { showToast, setActiveScreen, setModal } = useUI();
  const { setViewingProfile } = useAppStore();
  const queryClient = useQueryClient();
  const [isPurchasing, setIsPurchasing] = useState(false);

  // Swipe handler for quick actions
  const swipeProfile = useSwipeProfile({
    onSuccess: async (response, request) => {
      // Invalidate likes list to refresh after swipe
      queryClient.invalidateQueries(['likes']);
      
      // Invalidate notification counters to update the badge count
      queryClient.invalidateQueries(queryKeys.notificationCounters);
      
      // Invalidate feed to refresh when user returns to feed screen
      queryClient.invalidateQueries(queryKeys.feedProfiles);
      
      // If it's a match, show celebration modal
      if (response.matched) {
        // Refetch matches to get the new match
        await queryClient.invalidateQueries({ queryKey: queryKeys.matches });
        
        // Wait a bit for the query to refetch
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Get the updated matches from cache
        const matchesData = queryClient.getQueryData<any>(queryKeys.matches);
        
        // Find the match with this user
        let matchId: string | undefined;
        if (matchesData && Array.isArray(matchesData)) {
          const match = matchesData.find((m: any) => 
            m.users?.some((u: any) => u.id === request.targetId)
          );
          matchId = match?.id;
        }
        
        setModal('match-celebration', { 
          userId: request.targetId,
          matchId: matchId 
        });
      }
    },
    onError: () => {
      showToast('Не удалось выполнить действие', 'error');
    },
  });

  // Fetch subscription status and likes
  const { data: subscriptionData, isLoading: isLoadingSubscription } = useSubscriptionStatus();
  const { data: likesData, isLoading: isLoadingLikes } = useLikes({
    enabled: subscriptionData?.subscriptions.gold === true, // Only fetch likes if user has gold
  });

  const handlePremiumClick = async () => {
    try {
      setIsPurchasing(true);
      TelegramUtils.haptic.light();
      
      // Use PaymentHelper to handle full payment flow
      const success = await PaymentHelper.purchaseWithStars('gold_subscription');
      
      if (success) {
        // Payment confirmed - invalidate subscription status to refetch
        queryClient.invalidateQueries(['subscriptions', 'status']);
        
        // Also invalidate likes to load the full list
        queryClient.invalidateQueries(['likes']);
        
        showToast('🎉 Gold подписка активирована!', 'success');
        TelegramUtils.haptic.success();
      }
    } catch (error) {
      console.error('Purchase error:', error);
      showToast('Не удалось оформить подписку', 'error');
      TelegramUtils.haptic.error();
    } finally {
      setIsPurchasing(false);
    }
  };

  const handleCardClick = (userId: number) => {
    setViewingProfile(userId.toString());
    TelegramUtils.haptic.light();
  };

  const handleQuickSwipe = (userId: number, direction: 'like' | 'pass', event: React.MouseEvent) => {
    // Prevent card click event
    event.stopPropagation();
    
    const swipeDirection: 'like' | 'pass' | 'super_like' = direction;
    swipeProfile.mutate({
      targetId: userId.toString(),
      direction: swipeDirection,
    });
    
    TelegramUtils.haptic.light();
  };

  const formatLikeTime = (likedAt: string) => {
    const date = new Date(likedAt);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      return 'Только что';
    } else if (diffInHours < 24) {
      return `${diffInHours}ч назад`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays}д назад`;
    }
  };

  // Show loading state only for subscription check
  if (isLoadingSubscription) {
    return <LoadingScreen message="Загружаем..." />;
  }

  // Check if user has gold subscription
  const hasGold = subscriptionData?.subscriptions.gold === true;

  // If user has gold and likes are still loading
  if (hasGold && isLoadingLikes) {
    return <LoadingScreen message="Загружаем..." />;
  }

  // If no gold, show premium placeholder
  if (!hasGold) {
    return (
      <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
        <div className="w-full max-w-sm">
          {/* Premium Icon */}
          <div className="relative mb-8">
            <div className="w-28 h-28 mx-auto bg-gradient-to-br from-white/10 to-white/5 rounded-full flex items-center justify-center border border-white/20">
              <svg className="w-14 h-14 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            {/* Subtle glow effect */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-32 h-32 bg-white/5 rounded-full blur-xl" />
            </div>
          </div>

          {/* Title */}
          <h3 className="text-2xl font-bold text-white mb-3">
            ITinder Premium
          </h3>
          <p className="text-neutral-400 mb-8 text-sm leading-relaxed">
            Узнайте кому вы понравились и получите больше возможностей
          </p>

          {/* Blurred preview cards */}
          <div className="grid grid-cols-3 gap-3 mb-8">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="aspect-[3/4] bg-white/5 rounded-2xl flex items-center justify-center border border-white/10 backdrop-blur-sm relative overflow-hidden"
              >
                {/* Gradient overlay for blur effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent" />
                <div className="text-3xl relative z-10">❤️</div>
              </div>
            ))}
          </div>

          {/* Features list */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-5 mb-6 text-left">
            <div className="space-y-3 text-sm text-neutral-300">
              <div className="flex items-center gap-3">
                <div className="w-1.5 h-1.5 bg-white rounded-full" />
                <span>Просмотр всех лайков</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-1.5 h-1.5 bg-white rounded-full" />
                <span>Безлимитные супер-лайки</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-1.5 h-1.5 bg-white rounded-full" />
                <span>Отмена последних свайпов</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-1.5 h-1.5 bg-white rounded-full" />
                <span>Приоритет в показе</span>
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <button 
            onClick={handlePremiumClick}
            disabled={isPurchasing}
            className="w-full h-14 bg-gradient-to-r from-white to-neutral-200 text-black rounded-2xl font-bold text-base hover:from-neutral-100 hover:to-neutral-300 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPurchasing ? 'Оформление...' : 'Получить Premium'}
          </button>

          {/* Price hint */}
          {subscriptionData && subscriptionData.gold_price && (
            <p className="text-xs text-neutral-600 mt-4">
              {subscriptionData.gold_price} Telegram Stars / месяц
            </p>
          )}
        </div>
      </div>
    );
  }

  // User has gold - show likes
  const likes = likesData?.users || [];
  const likesCount = likesData?.count || 0;

  // Empty state with gold
  if (likes.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="text-6xl mb-4">💝</div>
        <h3 className="text-lg font-semibold text-white mb-2">Пока никто не лайкнул ваш профиль</h3>
        <p className="text-white/70 mb-6">
          Продолжайте свайпать, и скоро здесь появятся люди!
        </p>
        <button
          onClick={() => setActiveScreen('feed')}
          className="h-12 px-6 bg-white text-black rounded-2xl font-semibold hover:bg-neutral-100 transition-all active:scale-95"
        >
          Вернуться к свайпам
        </button>
      </div>
    );
  }

  // Show likes grid
  return (
    <div>
      {/* Likes Grid */}
      <div className="grid grid-cols-2 gap-3">
        {likes.map((liker: LikerUser) => (
          <div
            key={liker.user_id}
            className="bg-white/5 rounded-2xl overflow-hidden border border-white/10 flex flex-col"
          >
            {/* Photo - clickable to view profile */}
            <div 
              className="aspect-square relative cursor-pointer hover:opacity-90 transition-opacity"
              onClick={() => handleCardClick(liker.user_id)}
            >
              {liker.main_photo ? (
                <img
                  src={liker.main_photo}
                  alt={liker.first_name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-white/10 to-white/5 flex items-center justify-center">
                  <svg className="w-16 h-16 text-white/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
              )}
              
              {/* Superlike badge */}
              {liker.is_superlike && (
                <div className="absolute top-2 left-2 px-2 py-1 bg-blue-500 rounded-full flex items-center gap-1">
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                  <span className="text-white text-xs font-semibold">Супер</span>
                </div>
              )}
            </div>
            
            {/* Info and buttons */}
            <div className="p-3 flex flex-col gap-2">
              <div 
                className="cursor-pointer"
                onClick={() => handleCardClick(liker.user_id)}
              >
                <h4 className="font-semibold text-white truncate">
                  {liker.first_name}, {liker.age}
                </h4>
                <p className="text-xs text-white/50 truncate">
                  {formatLikeTime(liker.liked_at)}
                </p>
              </div>
              
              {/* Action buttons */}
              <div className="flex gap-2">
                <button
                  onClick={(e) => handleQuickSwipe(liker.user_id, 'pass', e)}
                  className="flex-1 h-9 rounded-xl bg-white/5 hover:bg-white/10 active:scale-95 transition-all flex items-center justify-center border border-white/10"
                  aria-label="Пропустить"
                >
                  <svg className="w-5 h-5 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                <button
                  onClick={(e) => handleQuickSwipe(liker.user_id, 'like', e)}
                  className="flex-1 h-9 rounded-xl bg-gradient-to-r from-white to-neutral-200 hover:from-neutral-100 hover:to-neutral-300 active:scale-95 transition-all flex items-center justify-center"
                  aria-label="Нравится"
                >
                  <svg className="w-5 h-5 text-black" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// My Likes List Component (outgoing likes)
const MyLikesList: React.FC = () => {
  const { showToast, setActiveScreen } = useUI();
  const { setViewingProfile } = useAppStore();
  const queryClient = useQueryClient();
  const removeLike = useRemoveLike();

  // Fetch my outgoing likes
  const { data: myLikesData, isLoading: isLoadingMyLikes } = useMyLikes();

  const handleCardClick = (userId: number) => {
    setViewingProfile(userId.toString());
    TelegramUtils.haptic.light();
  };

  const handleQuickRemove = (userId: number, event: React.MouseEvent) => {
    // Prevent card click event
    event.stopPropagation();
    
    removeLike.mutate({
      target_user_id: userId,
    });
  };

  const formatLikeTime = (likedAt: string) => {
    const date = new Date(likedAt);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      return 'Только что';
    } else if (diffInHours < 24) {
      return `${diffInHours}ч назад`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays}д назад`;
    }
  };

  // Show loading state
  if (isLoadingMyLikes) {
    return <LoadingScreen message="Загружаем..." />;
  }

  const myLikes = myLikesData?.users || [];
  const likesCount = myLikesData?.count || 0;

  // Empty state
  if (myLikes.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="text-6xl mb-4">💝</div>
        <h3 className="text-lg font-semibold text-white mb-2">Вы еще никому не поставили лайк</h3>
        <p className="text-white/70 mb-6">
          Начните свайпать, чтобы найти интересных людей!
        </p>
        <button
          onClick={() => setActiveScreen('feed')}
          className="h-12 px-6 bg-white text-black rounded-2xl font-semibold hover:bg-neutral-100 transition-all active:scale-95"
        >
          Вернуться к свайпам
        </button>
      </div>
    );
  }

  // Show likes grid
  return (
    <div>
      {/* Likes Grid */}
      <div className="grid grid-cols-2 gap-3">
        {myLikes.map((liked: MyLikesUser) => (
          <div
            key={liked.user_id}
            className="bg-white/5 rounded-2xl overflow-hidden border border-white/10 flex flex-col"
          >
            {/* Photo - clickable to view profile */}
            <div 
              className="aspect-square relative cursor-pointer hover:opacity-90 transition-opacity"
              onClick={() => handleCardClick(liked.user_id)}
            >
              {liked.main_photo ? (
                <img
                  src={liked.main_photo}
                  alt={liked.first_name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-white/10 to-white/5 flex items-center justify-center">
                  <svg className="w-16 h-16 text-white/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
              )}
            </div>
            
            {/* Info and remove button */}
            <div className="p-3 flex flex-col gap-2">
              <div 
                className="cursor-pointer"
                onClick={() => handleCardClick(liked.user_id)}
              >
                <h4 className="font-semibold text-white truncate">
                  {liked.first_name}, {liked.age}
                </h4>
                <p className="text-xs text-white/50 truncate">
                  {formatLikeTime(liked.liked_at)}
                </p>
              </div>
              
              {/* Remove button */}
              <button
                onClick={(e) => handleQuickRemove(liked.user_id, e)}
                disabled={removeLike.isLoading}
                className="w-full h-9 rounded-xl bg-white/5 hover:bg-white/10 active:scale-95 transition-all flex items-center justify-center border border-white/10 disabled:opacity-50"
                aria-label="Удалить"
              >
                <svg className="w-5 h-5 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Profile Detail for My Likes (with remove button)
const MyLikesProfileDetail: React.FC<{ userId: string; onBack: () => void }> = ({ userId, onBack }) => {
  const { showToast } = useUI();
  const removeLike = useRemoveLike();
  const [isRemoving, setIsRemoving] = useState(false);

  const handleRemove = async () => {
    const confirmed = await TelegramUtils.showConfirm(
      'Вы уверены, что хотите удалить лайк?'
    );

    if (!confirmed) return;

    setIsRemoving(true);
    removeLike.mutate(
      { target_user_id: Number(userId) },
      {
        onSuccess: () => {
          showToast('Лайк удален', 'success');
          onBack();
        },
        onError: () => {
          setIsRemoving(false);
        },
      }
    );
  };

  return (
    <ProfileDetailScreen
      userId={userId}
      onBack={onBack}
      onSwipe={() => {}} // No swipe functionality
      matchId={undefined}
      onUnmatch={handleRemove} // Use unmatch button slot for remove
    />
  );
};

export default MatchesScreen;
